import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LngLatBoundsLike } from "maplibre-gl";
import { Geometry } from "geojson";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CompareTableIcon } from "@/assets/icons";
import { Table } from "@/components/ui/table";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  BOUNDS_PADDING,
  TO_BOUNDS_DURATION
} from "@/hooks/map/interactions/useMapInteractions";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  removeComparisonFeatureByIndex,
  setComparisonFeature
} from "@/store/slice/mapSlice";
import { Dataset, DatasetAttributeSchema } from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import {
  transformPropertiesWithCategory,
  transformVisibleAttributes
} from "@/utils/dataset";

import { bbox } from "@turf/turf";

import { getDisplayValue } from "../utils";
import ComparePanelHeader from "./ComparePanelHeader";
import ComparePanelValue from "./ComparePanelValue";

const transformFeatureProperties = (
  feature: SelectedFeature,
  selectedDatasetCategories: Dataset,
  timeZone?: { code: string; name: string }
) => {
  if (!feature) return {};

  const categories = selectedDatasetCategories?.categories ?? [];

  const transformedProperties = transformPropertiesWithCategory(
    feature.properties as SelectedFeature,
    categories
  );

  const resultTransform = transformVisibleAttributes(
    transformedProperties,
    timeZone
  );

  return Object.entries(feature.properties ?? {}).reduce<
    Record<string, unknown>
  >((acc, [key, value]) => {
    // 1. Apply datetime transform when available, otherwise keep the raw value.
    const transformedValue = resultTransform[key] ?? value;

    // 2. Format numeric properties using the data_type from the dataset
    //    categories, matched by `attribute_name` against the property key.
    const dataType = categories.find(
      (category) => category.attribute_name === key
    )?.data_type;

    acc[key] = getDisplayValue(transformedValue, dataType);

    return acc;
  }, {});
};

export default function ComparePanelData() {
  const dispatch = useDispatch();
  const { handleClearSelectedState, map, zoomLevel } = useMap();
  const { data } = useGetUserProfileAPI();

  const { showComparisonPopup } = useSelector((state: RootState) => state.ui);
  const { selectedDataset } = useSelector((root: RootState) => root.dataset);
  const { comparisonFeatureData } = useSelector((root: RootState) => root.map);

  const handlePinned = (index: number) => {
    if (comparisonFeatureData.length > 0 && index !== 0) {
      const updatedFeatures = [...comparisonFeatureData];
      const featureToPin = updatedFeatures.splice(index, 1)[0];
      updatedFeatures.unshift(featureToPin);
      dispatch(setComparisonFeature(updatedFeatures));
    }
  };

  const handleRemoveCurrentFeatureData = (index: number) => {
    handleClearSelectedState(comparisonFeatureData[index]);
    dispatch(removeComparisonFeatureByIndex(index));
  };

  const handleZoomToTarget = (geometry: Geometry) => {
    const bounds = bbox(geometry);
    map?.fitBounds(bounds as LngLatBoundsLike, {
      padding: BOUNDS_PADDING,
      maxZoom: zoomLevel,
      duration: TO_BOUNDS_DURATION
    });
  };

  const referenceIdAttribute = useMemo(() => {
    return (
      selectedDataset?.categories.find((attr) => attr.is_reference_id)
        ?.attribute_name || ""
    );
  }, [selectedDataset?.categories]);

  const attributeIdHeader =
    selectedDataset?.selectedCategory?.attribute_name || "";

  const attributes = useMemo(() => {
    return selectedDataset?.categories.filter(
      (attr) =>
        attr.attribute_name !== referenceIdAttribute &&
        attr.attribute_name !== "Grid ID" &&
        attr.attribute_name !== "Catchment ID" &&
        attr.attribute_name !== "Polygon ID" &&
        attr.is_visible
    );
  }, [
    selectedDataset?.categories,
    referenceIdAttribute
  ]) as DatasetAttributeSchema[];

  const transformedFeatures = useMemo(() => {
    return comparisonFeatureData.map((feature, index) => {
      const transformed = transformFeatureProperties(
        feature,
        selectedDataset as Dataset,
        data?.timezone
      );
      return {
        ...feature,
        ...feature.properties,
        ...transformed,
        geometry: feature.geometry,
        index
      };
    });
  }, [comparisonFeatureData, data?.timezone, selectedDataset]);

  return (
    <div
      className="w-full px-2 pb-3 overflow-auto"
      data-testid="compare-panel-data"
    >
      <Table className="min-w-full border-separate table-fixed border-spacing-x-2">
        <thead>
          <tr>
            <th className="w-full pl-2 text-xl font-semibold leading-7 text-left truncate text-txtHeading">
              {selectedDataset?.data?.display_name || "Comparison Table"}
            </th>
            {transformedFeatures.map((feature, featureIndex) => (
              <th key={`head-${feature.id}`} className="w-full align-top">
                <ComparePanelHeader
                  geometry={comparisonFeatureData[featureIndex]?.geometry}
                  value={feature}
                  isPinned={featureIndex === 0}
                  index={featureIndex}
                  handlePinned={handlePinned}
                  handleRemoveCurrentFeatureData={
                    handleRemoveCurrentFeatureData
                  }
                  handleZoomToTarget={handleZoomToTarget}
                  attributeIdHeader={
                    showComparisonPopup.isFromAnalysisResult
                      ? `ID: ${
                          feature?.properties?.["Grid ID"] ??
                          feature?.properties?.["Catchment ID"] ??
                          feature?.properties?.["Polygon ID"] ??
                          feature?.properties?.[attributeIdHeader]
                        }`
                      : ""
                  }
                />
              </th>
            ))}
            {transformedFeatures.length < 4 &&
              Array.from({ length: 4 - transformedFeatures.length }).map(
                (_, placeholderIndex) => (
                  <th
                    key={`head-placeholder-${transformedFeatures.length + placeholderIndex}`}
                    className="w-full"
                  />
                )
              )}
          </tr>
        </thead>
        <tbody>
          {attributes.map((attribute, attributeIndex) => (
            <tr key={`row-${attribute.id}-${attributeIndex}`}>
              <td className="w-full text-sm font-semibold align-top text-txtHeading">
                <div
                  key={`${attribute.id}-${attributeIndex}`}
                  className={cn(
                    "rounded-lg w-full flex flex-col h-[50px] truncate hover:text-clip hover:overflow-auto",
                    attributeIndex % 2 !== 0 && "bg-neutral-50"
                  )}
                >
                  <span className="px-2 py-3 text-sm font-semibold leading-5 text-left text-txtHeading">
                    {attribute.attribute_name}
                  </span>
                </div>
              </td>
              {transformedFeatures.map((feature, featureIndex) => (
                <td
                  key={`val-${feature.id}-${featureIndex}`}
                  className="w-full align-top"
                >
                  <ComparePanelValue
                    geometry={feature.geometry}
                    value={feature}
                    attribute={attribute}
                    isPinned={featureIndex === 0}
                    index={featureIndex}
                    attributeIndex={attributeIndex}
                  />
                </td>
              ))}
              {transformedFeatures.length < 2 && attributeIndex === 0 && (
                <td
                  rowSpan={attributes.length}
                  colSpan={5 - transformedFeatures.length}
                  className="italic text-center text-gray-400 align-top"
                  style={{
                    minWidth: `${(4 - transformedFeatures.length) * 200}px`
                  }}
                >
                  <div className="flex flex-col items-center justify-center h-full pt-4">
                    <CompareTableIcon fill="#CECECE" />
                    <p className="text-[1.25rem] text-light-gray leading-8 text-center mt-2">
                      Please select point to grid as data comparison.
                    </p>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
